/**
 * AI CONTEXT
 * Purpose: draggable/resizable selection overlay for timeline time window.
 * Inputs: top/height/drag state and drag+resize callbacks.
 * Used by: ClassScheduleTimeline.
 * Refactor: move interaction styles and handle metrics to constants.
 */
import { MouseEvent } from "react"
import styled from "styled-components"

// Styled selection box container
const StyledSelectionBox = styled.div<{
  isDragging: boolean
  isResizing: boolean
}>`
  position: absolute;
  // Add a border to the left and right of the selection box
  left: -3px;
  right: -3px;

  width: auto;
  background-color: ${(props) =>
    props.isDragging || props.isResizing
      ? "rgba(24, 144, 255, 0.2)"
      : "rgba(24, 144, 255, 0.1)"};
  border: 2px solid #1890ff;
  border-radius: 4px;
  z-index: 100;
  pointer-events: none;
  user-select: none;
`

// Styled resize handles
const StyledResizeHandle = styled.div<{
  isActive: boolean
  position: "top" | "bottom"
}>`
  position: absolute;
  ${(props) => props.position}: -4px;
  left: 0;
  right: 0;
  height: 6px;
  cursor: ns-resize;
  background-color: ${(props) => (props.isActive ? "#1890ff" : "transparent")};
  border-radius: ${(props) =>
    props.position === "top" ? "4px 4px 0 0" : "0 0 4px 4px"};
  pointer-events: auto;

  &:hover {
    background-color: #1890ff;
  }
`

// Styled draggable handle
const StyledDragHandle = styled.div<{ isDragging: boolean }>`
  position: absolute;
  top: 50%;
  right: -10px;
  transform: translate(8px, -50%);
  width: 12px;
  height: 50px;
  cursor: ${(props) => (props.isDragging ? "grabbing" : "grab")};
  pointer-events: auto;
  background-color: rgba(24, 144, 255, 0.5);
  border: 1px solid #1890ff;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: #fff;
  font-weight: bold;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  writing-mode: vertical-rl;
`

interface DraggableSelectionAreaProps {
  top: number
  height: number
  isDragging: boolean
  isResizing: "top" | "bottom" | null
  onDragStart: (e: MouseEvent) => void
  onResizeStart: (edge: "top" | "bottom", e: MouseEvent) => void
}

const DraggableSelectionArea = ({
  top,
  height,
  isDragging,
  isResizing,
  onDragStart,
  onResizeStart,
}: DraggableSelectionAreaProps) => {
  return (
    <StyledSelectionBox
      style={{ top, height }}
      isDragging={isDragging}
      isResizing={!!isResizing}
    >
      {/* Draggable center area */}
      <StyledDragHandle isDragging={isDragging} onMouseDown={onDragStart}>
        ⋮⋮⋮
      </StyledDragHandle>

      {/* Top resize handle */}
      <StyledResizeHandle
        position="top"
        isActive={isResizing === "top"}
        onMouseDown={(e) => onResizeStart("top", e)}
      />

      {/* Bottom resize handle */}
      <StyledResizeHandle
        position="bottom"
        isActive={isResizing === "bottom"}
        onMouseDown={(e) => onResizeStart("bottom", e)}
      />
    </StyledSelectionBox>
  )
}

export default DraggableSelectionArea
